import React, { useEffect, useRef } from 'react';
import { Box } from '@ktag/styleguide/dist/react';
import { useTranslation } from 'react-i18next';
import { Coordinate } from 'ol/coordinate';
import LandownerRequestButton from './LandownerRequestButton';
import IdentifyApplicationLinks from './IdentifyApplicationLinks';
import IdentifyGeneralInfoResult from './IdentifyGeneralInfoResult';
import IdentifyMessageBox from './IdentifyMessageBox';
import useAppState from '../../../AppStore';
import { useIdentifyLocationlInfo } from '../api/useIdentify';
import './identifyGeneralInfo.scss';
import { useTracking } from '../../../utils/tracking/TrackingContext';
import IdentifyGeneralInfoPlaceholder from './IdentifyGeneralInfoPlaceholder';
import { DefaultResultProperties } from '../api/contract';

interface IdentifyGeneralInfoProps {
  identifyCoordinate: Coordinate;
  tolerance: number;
}

const IdentifyGeneralInfo = ({
  identifyCoordinate,
  tolerance,
}: IdentifyGeneralInfoProps) => {
  const { t } = useTranslation('agisviewer');
  const landownerNavigation = useAppState((state) => state.landownerNavigation);
  const setActiveFlyinView = useAppState((state) => state.setActiveFlyinView);
  const setIdentifyDefaultResultProperties = useAppState(
    (state) => state.setIdentifyDefaultResultProperties,
  );

  const { Tracking } = useTracking();

  const parentRef = useRef<HTMLDivElement>(null);

  const landownerRequest = (
    identifyDefaultResultProperties: DefaultResultProperties | null,
  ) => {
    landownerNavigation.length = 0;
    if (identifyDefaultResultProperties?.parcelEgrid) {
      Tracking.Landowner.clickLandownerButton(
        identifyDefaultResultProperties.parcelEgrid,
      );
      setIdentifyDefaultResultProperties(identifyDefaultResultProperties);
      setActiveFlyinView('landowner');
    }
  };

  const {
    isLoading,
    isError,
    error,
    data: identifyResult,
  } = useIdentifyLocationlInfo(identifyCoordinate, tolerance);

  useEffect(() => {
    if (isError) {
      Tracking.Error.identifyLocationInfoError(
        identifyCoordinate.toString(),
        error.message,
      );
    }
  }, [isError]);

  const inKantonAG = identifyResult?.properties?.isCoordinateWithinCanton ?? true;

  if (isLoading) {
    return (
      <Box type="highlight" additionalClassNames="identify-general-info">
        <IdentifyGeneralInfoPlaceholder />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box type="highlight" additionalClassNames="identify-general-info">
        <IdentifyMessageBox message={t('identify_error_message')} type="negative" />
      </Box>
    );
  }

  return (
    <Box type="highlight" additionalClassNames="identify-general-info">
      <IdentifyGeneralInfoResult
        coordinate={identifyResult?.label ?? ''}
        municipality={identifyResult?.properties?.municipality ?? ''}
        elevation={identifyResult?.properties?.elevation ?? ''}
        address={identifyResult?.properties?.address ?? ''}
        parcel={identifyResult?.properties?.parcel ?? ''}
        inKantonAG={inKantonAG}
      />
      <div ref={parentRef}>
        <LandownerRequestButton
          ref={parentRef}
          onClick={() =>
            landownerRequest(
              identifyResult?.properties || ({} as DefaultResultProperties),
            )
          }
          disabled={!inKantonAG}
        />
      </div>
      {inKantonAG && (
        <IdentifyApplicationLinks
          egrid={identifyResult?.properties?.parcelEgrid ?? ''}
          agobisLink={identifyResult?.properties?.agobisLink ?? ''}
          identifyCoordinate={identifyResult?.identifyPoint ?? { x: 0, y: 0 }}
        />
      )}
    </Box>
  );
};

export default IdentifyGeneralInfo;
